import {
  useAdminStats,
  useAdminTopServices,
  useAdminRecentTransactions,
} from "../../../api/admin";

export default function AdminDashboard() {
  const {
    data: statsData,
    isLoading: statsLoading,
    refetch: refetchStats,
  } = useAdminStats();
  const { data: topServicesData, isLoading: servicesLoading } =
    useAdminTopServices();
  const { data: recentTransactionsData, isLoading: transactionsLoading } =
    useAdminRecentTransactions();

  // Map API response to component state
  const stats = {
    total_users: statsData?.user_count || 0,
    active_users: statsData?.user_count || 0, // Using user_count as active users for now
    total_transactions: statsData?.transaction_count || 0,
    total_revenue: statsData?.total_revenue || 0,
    total_deposit: statsData?.total_deposit || 0,
    total_transaction: statsData?.total_transaction || 0,
    pending_transactions: 0, // Not provided in API response
    success_rate: 0, // Not provided in API response
    monthly_growth: 0, // Not provided in API response
    new_users_today: 0, // Not provided in API response
  };

  const topServices = topServicesData || [];
  const recentTransactions = recentTransactionsData || [];

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
      case "success":
        return "bg-purple-100 text-purple-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "failed":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleRefresh = () => {
    refetchStats();
  };

  const isLoading = statsLoading || servicesLoading || transactionsLoading;

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Admin Dashboard
          </h1>
          <p className="text-gray-600 mt-1">
            Monitor platform performance and manage operations
          </p>
        </div>
        <div className="flex items-center space-x-3 mt-4 md:mt-0">
          <button className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
            <i className="ri-download-fill mr-2"></i>
            Export Report
          </button>
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <i
              className={`ri-refresh-fill mr-2 ${isLoading ? "animate-spin" : ""}`}
            ></i>
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      {statsLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 animate-pulse"
            >
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-3"></div>
              <div className="h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-1/3"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {/* Total Users */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Users</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {stats.total_users.toLocaleString()}
                </p>
                <p className="text-sm text-gray-500 mt-1">Registered users</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <i className="ri-user-fill text-blue-600 text-xl"></i>
              </div>
            </div>
          </div>

          {/* Total Transactions */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Total Transactions
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {stats.total_transactions.toLocaleString()}
                </p>
                <p className="text-sm text-gray-500 mt-1">All time</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <i className="ri-file-list-3-fill text-purple-600 text-xl"></i>
              </div>
            </div>
          </div>

          {/* Total Deposits */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Total Deposits
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  ₦{stats.total_deposit.toLocaleString()}
                </p>
                <p className="text-sm text-purple-600 mt-1">
                  <i className="ri-arrow-down-fill mr-1"></i>
                  Wallet funding
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <i className="ri-wallet-3-fill text-purple-600 text-xl"></i>
              </div>
            </div>
          </div>

          {/* Total Transaction Amount */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Transaction Volume
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  ₦{stats.total_transaction.toLocaleString()}
                </p>
                <p className="text-sm text-orange-600 mt-1">
                  <i className="ri-exchange-line mr-1"></i>
                  Total processed
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <i className="ri-money-dollar-circle-fill text-orange-600 text-xl"></i>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Charts and Tables Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Services */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              Top Services
            </h3>
            <button className="text-sm text-red-600 hover:text-red-700">
              View All
            </button>
          </div>
          {servicesLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg animate-pulse"
                >
                  <div className="flex items-center space-x-3 flex-1">
                    <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                  <div className="w-20 h-4 bg-gray-200 rounded"></div>
                </div>
              ))}
            </div>
          ) : topServices.length > 0 ? (
            <div className="space-y-4">
              {topServices.map((service: any, index: number) => (
                <div
                  key={service.type || index}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                      <span className="text-sm font-bold text-red-600">
                        {index + 1}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {service.type || service.service_name}
                      </p>
                      <p className="text-sm text-gray-500">
                        {service.total?.toLocaleString() ||
                          service.count?.toLocaleString() ||
                          "0"}{" "}
                        transactions
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">
                      ₦
                      {service.revenue?.toLocaleString() ||
                        service.total_revenue?.toLocaleString() ||
                        "0"}
                    </p>
                    {service.growth !== undefined && (
                      <p className="text-sm text-purple-600">
                        +{service.growth}%
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <i className="ri-bar-chart-line text-4xl mb-2"></i>
              <p>No service data available</p>
            </div>
          )}
        </div>

        {/* Recent Transactions */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              Recent Transactions
            </h3>
            <button className="text-sm text-red-600 hover:text-red-700">
              View All
            </button>
          </div>
          {transactionsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-3 rounded-lg animate-pulse"
                >
                  <div className="flex items-center space-x-3 flex-1">
                    <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                  <div className="w-20 h-4 bg-gray-200 rounded"></div>
                </div>
              ))}
            </div>
          ) : recentTransactions.length > 0 ? (
            <div className="space-y-3">
              {recentTransactions.slice(0, 5).map((transaction: any) => (
                <div
                  key={transaction.id}
                  className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                      <i className="ri-user-fill text-gray-600"></i>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {transaction.profile?.full_name ||
                          transaction?.username ||
                          "User"}
                      </p>
                      <p className="text-sm text-gray-500">
                        {transaction.type || transaction.transaction_type} •{" "}
                        {transaction.time ||
                          transaction.created_at.slice(0, 10)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">
                      ₦{transaction.amount?.toLocaleString() || "0"}
                    </p>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(transaction.status)}`}
                    >
                      {transaction.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <i className="ri-file-list-line text-4xl mb-2"></i>
              <p>No recent transactions</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Quick Actions
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {[
            {
              name: "Add User",
              icon: "ri-user-add-fill",
              color: "bg-blue-500",
              path: "/admin/users",
            },
            {
              name: "View Transactions",
              icon: "ri-file-list-3-fill",
              color: "bg-purple-500",
              path: "/admin/transactions",
            },
            {
              name: "Manage Networks",
              icon: "ri-wifi-fill",
              color: "bg-purple-500",
              path: "/admin/networks",
            },
            {
              name: "Data Plans",
              icon: "ri-database-2-fill",
              color: "bg-orange-500",
              path: "/admin/data-plans",
            },
            {
              name: "Platform Settings",
              icon: "ri-settings-3-fill",
              color: "bg-gray-500",
              path: "/admin/settings",
            },
            {
              name: "API Providers",
              icon: "ri-code-s-slash-fill",
              color: "bg-indigo-500",
              path: "/admin/api-providers",
            },
          ].map((action) => (
            <button
              key={action.name}
              className="flex flex-col items-center p-4 rounded-lg hover:bg-gray-50 transition-colors group"
            >
              <div
                className={`w-12 h-12 ${action.color} rounded-lg flex items-center justify-center mb-2 group-hover:scale-105 transition-transform`}
              >
                <i className={`${action.icon} text-white text-xl`}></i>
              </div>
              <span className="text-sm font-medium text-gray-700 text-center">
                {action.name}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
